
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { verifyUserRoles } from "@/utils/roleVerifier";
import { TranslationText } from "@/components/i18n/TranslationText";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCcw, ShieldAlert, CheckCircle } from "lucide-react";

export function AdminDebugTool() {
  const queryClient = useQueryClient();
  const [isChecking, setIsChecking] = useState(false);
  const [result, setResult] = useState<any>(null);
  
  const handleVerifyRoles = async () => {
    setIsChecking(true);
    try {
      const verificationResult = await verifyUserRoles();
      setResult(verificationResult);
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : String(error)
      });
    } finally {
      setIsChecking(false);
    }
  };
  
  const handleInvalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["hasRole"] });
    queryClient.invalidateQueries({ queryKey: ["userRoles"] });
    setResult(prevResult => ({
      ...prevResult,
      queriesInvalidated: true
    }));
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <TranslationText i18nKey="admin.debug.title">
            Admin Access Debug Tool
          </TranslationText>
        </CardTitle>
        <CardDescription>
          <TranslationText i18nKey="admin.debug.description">
            Use this tool to verify your admin permissions directly with Supabase
          </TranslationText>
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {isChecking ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : result ? (
          <div className="space-y-4">
            <Alert variant={result.success ? "default" : "destructive"}>
              <AlertDescription className="flex items-center">
                {result.success ? (
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                ) : (
                  <ShieldAlert className="h-4 w-4 mr-2" />
                )}
                {result.success ? "Role verification completed successfully" : "Error verifying roles"}
              </AlertDescription>
            </Alert>
            
            {result.roles && (
              <div className="mt-4">
                <h4 className="text-sm font-medium mb-2">Your roles:</h4>
                <div className="flex flex-wrap gap-2">
                  {result.roles.map((role: string) => (
                    <span key={role} className="px-2 py-1 bg-primary/10 rounded-md text-xs font-medium">
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            <div className="mt-4">
              <h4 className="text-sm font-medium mb-2">Results:</h4>
              <div className="bg-muted/20 p-3 rounded-md">
                <pre className="text-xs overflow-auto whitespace-pre-wrap">
                  {JSON.stringify({
                    hasAdminRole: result.hasAdmin,
                    hasDeveloperRole: result.hasDeveloper,
                    hasTesterRole: result.hasTester,
                    queriesInvalidated: result.queriesInvalidated || false,
                    error: result.error,
                  }, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-muted-foreground text-sm">
            <TranslationText i18nKey="admin.debug.prompt">
              Click the button below to verify your admin permissions
            </TranslationText>
          </p>
        )}
      </CardContent>
      
      <CardFooter className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={handleVerifyRoles}
          disabled={isChecking}
        >
          {isChecking ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <ShieldAlert className="mr-2 h-4 w-4" />
          )}
          <TranslationText i18nKey="admin.debug.verify">
            Verify Admin Access
          </TranslationText>
        </Button>
        
        <Button
          variant="secondary"
          onClick={handleInvalidateQueries}
          disabled={isChecking}
        >
          <RefreshCcw className="mr-2 h-4 w-4" />
          <TranslationText i18nKey="admin.debug.refresh">
            Refresh Role Cache
          </TranslationText>
        </Button>
      </CardFooter>
    </Card>
  );
}
